import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Paperclip, MessageSquare } from 'lucide-react';
import { staffClient } from 'clients/staff';
import { articleApi, Article } from 'apis/article';
import Table, { Column } from 'ui/Table';
import Loading from 'ui/Loading';
import PageContainer from 'ui/PageContainer';
import PageHeader from 'ui/PageHeader';
import FilterSection from 'ui/FilterSection';
import TableContainer from 'ui/TableContainer';
import SearchInput from 'ui/SearchInput';
import { Badge, Button } from 'ui/shadcn';
import { cn } from 'lib/utils';
import { toast } from 'react-toastify';

interface NoticeBoard {
    id: number;
    name: string;
    description: string;
    board_type: string;
    categories?: Array<{
        id: number;
        name: string;
        order: number;
    }>;
}

const isNewArticle = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffDays = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays <= 7;
};

const NoticePage: React.FC = () => {
    const navigate = useNavigate();
    const [board, setBoard] = useState<NoticeBoard | null>(null);
    const [notices, setNotices] = useState<Article[]>([]);
    const [importantNotices, setImportantNotices] = useState<Article[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

    useEffect(() => {
        fetchNoticeBoard();
    }, []);

    useEffect(() => {
        if (board) fetchNotices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [board, selectedCategory]);

    const fetchNoticeBoard = async () => {
        try {
            const response = await staffClient.get('/board/notice');
            setBoard(response.data);
        } catch (error) {
            console.error('[Notice] fetchNoticeBoard error:', error);
            toast.error('공지사항을 불러오는데 실패했습니다.');
        }
    };

    const fetchNotices = async () => {
        if (!board) return;
        try {
            setLoading(true);
            const params: any = { boardId: board.id, limit: 1000 };
            if (selectedCategory) params.categoryId = selectedCategory;

            const response = await articleApi.getArticles(params);
            const articles = response.data?.data || response.data || [];

            const important = articles.filter((a: Article) => a.is_important);
            const normal = articles.filter((a: Article) => !a.is_important);
            setImportantNotices(important);
            setNotices(normal);
        } catch (error) {
            console.error('[Notice] fetchNotices error:', error);
            setNotices([]);
            setImportantNotices([]);
        } finally {
            setLoading(false);
        }
    };

    const handleWriteClick = () => {
        if (board) navigate(`/boards/${board.id}/articles/editor`);
    };

    const handleNoticeClick = (notice: Article) => {
        if (board) navigate(`/boards/${board.id}/articles/${notice.id}`);
    };

    const getCategoryVariant = (
        name: string
    ): 'default' | 'secondary' | 'success' | 'warning' | 'danger' | 'info' => {
        const n = name.toLowerCase();
        if (n.includes('긴급') || n.includes('중요')) return 'danger';
        if (n.includes('공지') || n.includes('안내')) return 'info';
        if (n.includes('업데이트') || n.includes('변경')) return 'success';
        if (n.includes('이벤트') || n.includes('행사')) return 'warning';
        return 'secondary';
    };

    const columns: Column<Article>[] = [
        {
            key: 'id',
            header: '번호',
            width: '70px',
            align: 'center',
            render: (value) => (
                <span className="text-sm text-muted-foreground tabular-nums">{value}</span>
            ),
        },
        {
            key: 'title',
            header: '제목',
            render: (value, row) => (
                <div className="flex items-center gap-2">
                    <span className={cn(row.is_important && 'font-semibold text-foreground')}>
                        {value}
                    </span>
                    {row.files && row.files.length > 0 && (
                        <Paperclip className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                    )}
                    {isNewArticle(row.created_at) && (
                        <span className="inline-flex items-center justify-center h-4 px-1 bg-destructive text-destructive-foreground text-[10px] font-bold rounded leading-none">
                            N
                        </span>
                    )}
                    {row.comment_count > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
                            <MessageSquare className="h-3 w-3" />
                            {row.comment_count}
                        </span>
                    )}
                </div>
            ),
        },
        {
            key: 'writer',
            header: '작성자',
            width: '120px',
            align: 'center',
            render: (_value, row) => {
                if (row.writer?.name) return row.writer.name;
                if (row.writer_id) return `사용자${row.writer_id}`;
                return <span className="text-neutral-400">알 수 없음</span>;
            },
        },
        {
            key: 'category_id',
            header: '카테고리',
            width: '120px',
            render: (_value, row) => {
                const category = board?.categories?.find((c) => c.id === row.category_id);
                const categoryName = category?.name || '일반';
                return (
                    <Badge variant={getCategoryVariant(categoryName)}>{categoryName}</Badge>
                );
            },
        },
        {
            key: 'is_public',
            header: '공개',
            width: '80px',
            align: 'center',
            render: (_, row) =>
                row.is_public !== false ? (
                    <Badge variant="success">공개</Badge>
                ) : (
                    <Badge variant="secondary">비공개</Badge>
                ),
        },
        {
            key: 'created_at',
            header: '작성일',
            width: '120px',
            align: 'center',
            sortable: true,
            render: (value) => {
                const date = new Date(value);
                const now = new Date();
                const diffDays = Math.floor(
                    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
                );

                if (diffDays === 0) {
                    return (
                        <span className="text-info font-medium tabular-nums">
                            {date.toLocaleTimeString('ko-KR', {
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                        </span>
                    );
                } else if (diffDays < 7) {
                    return (
                        <span className="text-muted-foreground tabular-nums">{diffDays}일 전</span>
                    );
                }
                return (
                    <span className="text-muted-foreground tabular-nums">
                        {date.toLocaleDateString('ko-KR').replace(/\. /g, '.').replace(/\.$/, '')}
                    </span>
                );
            },
        },
    ];

    const matchesSearch = (notice: Article) => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        const writerName = notice.writer?.name || '';
        return (
            notice.title.toLowerCase().includes(term) ||
            writerName.toLowerCase().includes(term)
        );
    };

    const allNotices = [...importantNotices, ...notices];
    const filteredAllNotices = allNotices.filter(matchesSearch).sort((a, b) => {
        if (a.is_important && !b.is_important) return -1;
        if (!a.is_important && b.is_important) return 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    if (!board && loading) {
        return (
            <PageContainer>
                <Loading text="공지사항을 불러오는 중..." />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title="공지사항"
                subtitle="회사 공지사항 및 중요 알림을 확인합니다."
                actions={
                    <Button onClick={handleWriteClick}>
                        <Plus />
                        글쓰기
                    </Button>
                }
            />

            <FilterSection>
                <SearchInput
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="제목 또는 작성자 검색..."
                />

                {board?.categories && board.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                        <CategoryChip
                            active={!selectedCategory}
                            onClick={() => setSelectedCategory(null)}
                        >
                            전체
                        </CategoryChip>
                        {board.categories
                            .sort((a, b) => a.order - b.order)
                            .map((cat) => (
                                <CategoryChip
                                    key={cat.id}
                                    active={selectedCategory === cat.id}
                                    onClick={() => setSelectedCategory(cat.id)}
                                >
                                    {cat.name}
                                </CategoryChip>
                            ))}
                    </div>
                )}
            </FilterSection>

            <TableContainer title="공지사항" resultCount={filteredAllNotices.length}>
                <Table
                    columns={columns}
                    data={filteredAllNotices}
                    pageSize={10}
                    pagination
                    loading={loading}
                    emptyMessage="등록된 공지사항이 없습니다."
                    stickyHeader
                    onRowClick={handleNoticeClick}
                    rowClassName={(row) =>
                        row.is_important
                            ? 'bg-amber-50/40 hover:!bg-amber-50'
                            : ''
                    }
                />
            </TableContainer>
        </PageContainer>
    );
};

const CategoryChip: React.FC<{
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
}> = ({ active, onClick, children }) => (
    <button
        type="button"
        onClick={onClick}
        className={cn(
            'inline-flex items-center h-8 px-3 rounded text-sm font-medium transition-colors duration-fast',
            active
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-neutral-700 hover:bg-accent hover:text-primary'
        )}
    >
        {children}
    </button>
);

export default NoticePage;
